import React, { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import ClientPicker from "@/components/ClientPicker";
import AssistantWidget from "@/components/AssistantWidget";
import ViewAsBanner from "@/components/ViewAsBanner";
import UnsyncedFilesBanner from "@/components/UnsyncedFilesBanner";
import { ClientProvider } from "@/contexts/ClientContext";

// Explicit allow-list: only show the header ClientPicker on routes that
// actually consume `useClients().selected`. Every admin route either takes
// the client from the URL (via ClientDetailLayout) or enumerates the full
// clients list itself, so the picker is only useful on client-portal pages.
const ROUTES_WITH_CLIENT_PICKER = ["/portal"];

export default function AppShell() {
  const { pathname } = useLocation();
  const [inViewAsMode, setInViewAsMode] = useState(
    () => !!sessionStorage.getItem("viewAsClientId"));
  // Re-read on every route change so Exit (which navigates) re-evaluates.
  useEffect(() => {
    setInViewAsMode(!!sessionStorage.getItem("viewAsClientId"));
  }, [pathname]);
  // Scroll the page-content pane back to the top on every route change
  // so users don't land halfway down a client's detail page just because
  // the previous page (Clients list) was scrolled.
  const contentRef = useRef(null);
  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
    // Also reset window scroll for routes that don't use the pane
    // (e.g. print previews).
    window.scrollTo({ top: 0, left: 0 });
  }, [pathname]);
  const showPicker = !inViewAsMode
    && ROUTES_WITH_CLIENT_PICKER.some((p) => pathname === p || pathname.startsWith(p + "/"));
  return (
    <ClientProvider>
      <div className="flex h-screen bg-zinc-50 print:block print:h-auto print:bg-white">
        {!inViewAsMode && (
          <div className="print:hidden">
            <Sidebar />
          </div>
        )}
        <main className="flex-1 flex flex-col overflow-hidden print:overflow-visible">
          <header
            className="h-16 backdrop-blur-xl bg-white/70 backdrop-saturate-150 border-b border-zinc-200 flex items-center justify-between px-8 z-30 print:hidden"
            data-testid="app-header"
          >
            <div className="flex items-center gap-5">
              <div>
                <div className="label-overline">Marketing Intelligence</div>
                <div className="font-display font-bold text-lg tracking-tight text-zinc-900 -mt-0.5">Console</div>
              </div>
              {showPicker && (
                <>
                  <div className="h-8 w-px bg-zinc-200" aria-hidden="true" />
                  <ClientPicker />
                </>
              )}
            </div>
          </header>
          <ViewAsBanner />
          {!inViewAsMode && <UnsyncedFilesBanner />}
          <div ref={contentRef}
            className="flex-1 overflow-y-auto p-6 lg:p-8 print:overflow-visible print:p-0"
            data-testid="page-content">
            <Outlet />
          </div>
          <div className="print:hidden">
            <AssistantWidget />
          </div>
        </main>
      </div>
    </ClientProvider>
  );
}
